import React, { useEffect, useState } from 'react';
import { useGetDepositInvoicesQuery } from '../../../redux/api/modules/billingApi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomTable from '../../../shared/CustomTable';
import CustomPagination from '../../../shared/CustomPagination';
import ReuseableModal from '../../../shared/ReuseableModal';
import { DepositColumns } from '../../../constants/BillingConstants';
import { EditIcon, PrintIcon } from '../../../utils/TableActionIcons';
import BillingFilters from '../../../components/his/billing/BillingFilters';
import CancelDepositBalanceForm from '../depost/CancelDepositBalanceForm';
import { MdCancel } from 'react-icons/md';
import usePersistentBillingFilters from '../../../hooks/usePersistentBillingFilters';
import { IoIosSend } from 'react-icons/io';
import { FaHistory } from 'react-icons/fa';
import { fetchData } from '../../../helpers/fetchData';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import CheckPermission from '../../../hooks/CheckPermission';
import ZatcaHistoryModal from '../../../components/his/billing/ZatcaHistoryModal';
import { hasBillingSearchFilters, omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from '../../../utils/businessLevelAccess';

const ConfirmationModal = React.lazy(() => import('../../../shared/ConfirmationModal'));

function DepositList() {
  const authUser = useSelector(state => state?.auth?.user);
  const skipInitialBillingLoad = shouldSkipInitialBillingLoad(authUser);
  const [payload, setPayload, resetPayload, appliedPayload, applyFilters] = usePersistentBillingFilters('deposit', { per_page: 20 });

  const {
    data,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetDepositInvoicesQuery(appliedPayload, {
    skip: skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)
  });

  const loading = queryLoading || queryFetching;

  const [searchQuery, setSearchQuery] = useState(payload?.query || '');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openCancelBalanceModal, setOpenCancelBalanceModal] = useState(false);
  const [openSendToZatcaModal, setOpenSendToZatcaModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [zatcaLoading, setZatcaLoading] = useState(false);
  const [openZatcaHistoryModal, setOpenZatcaHistoryModal] = useState(false);
  const [zatcaHistoryData, setZatcaHistoryData] = useState(null);
  const [zatcaHistoryLoading, setZatcaHistoryLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = CheckPermission();
  const isSendToZatca = useSelector(state => state?.auth?.zatcaSettings?.enable_send_deposit_invoices);
  const isHaveUpdateInvoicePermission = hasPermission('invoices', 'update');
  const isEnabledEditAfterZatca = useSelector(state => state?.auth?.zatcaSettings?.enable_edit_zatca_posted_invoice);

  const fetchInitialData = () => {
    refetch();
  };

  

  const onPageChange = async (newPage, newRowsPerPage) => {
    setPayload(prevPayload => ({
      ...prevPayload,
      page: newPage,
      per_page: newRowsPerPage
    }));
  };

  const getZatcaHistory = async invoiceNumber => {
    setOpenZatcaHistoryModal(true);
    setZatcaHistoryLoading(true);
    setSelectedInvoice(invoiceNumber);
    try {
      const response = await fetchData('GET', `/v3/zatca/history/deposit/${invoiceNumber}`);
      if (response?.success) {
        setZatcaHistoryData(response);
      } else {
        toast.error(response?.message || 'Failed to fetch ZATCA history');
        setOpenZatcaHistoryModal(false);
      }
    } catch (error) {
      toast.error('Failed to fetch ZATCA history');
      setOpenZatcaHistoryModal(false);
    } finally {
      setZatcaHistoryLoading(false);
    }
  };

  const handleSendToZatca = async () => {
    if (!selectedInvoice?.deposit_invoice_number) {
      toast.error('No invoice number found.');
      return;
    }
    setZatcaLoading(true);
    const response = await fetchData('POST', '/v3/zatca/sendDepositInvoiceToZatca', {
      deposit_invoice_number: selectedInvoice?.deposit_invoice_number
    });
    if (response?.message) {
      setOpenSendToZatcaModal(false);
      setSelectedInvoice(null);
      toast.success(response?.message);
      fetchInitialData();
    }
    setZatcaLoading(false);
  };

  const actionBodyTempelate = rowData => {
    const isCancelled = rowData?.status?.toLowerCase() === 'cancelled';
    const depositInvoiceNumber = rowData?.deposit_invoice_number || rowData?.invoice_number;
    return (
      <div className='flex gap-2'>
        <PrintIcon
          title={t('PrintInvoice')}
          iconSize='20'
          onClick={() =>
            navigate(`/print-invoice/${depositInvoiceNumber}`, {
              state: {
                invoiceNumber: depositInvoiceNumber,
                deposit: true,
                activeTabIndex: 4,
                activeChildTabIndex: 0
              }
            })
          }
        />
        {!isCancelled && isHaveUpdateInvoicePermission && (
          <>
            {(rowData?.zatca_status !== 'REPORTED' ||
              (rowData?.zatca_status === 'REPORTED' && isEnabledEditAfterZatca)) && (
              <EditIcon
                title={t('EditInvoice')}
                iconSize='18'
                onClick={() => {
                  navigate(`/billing/edit-deposit-invoice/${rowData?.invoice_number}`, {
                    state: {
                      type: 'deposit',
                      returnTo: '/billing',
                      previousState: {
                        page: payload.page,
                        per_page: payload.per_page,
                        filters: payload.filters,
                        activeInvoiceTabIndex: 4
                      }
                    }
                  });
                }}
              />
            )}



            <MdCancel
              title={t('CancelInvoice')}
              className='text-red-500 cursor-pointer disabled:text-blue/20'
              size='18'
              onClick={() => {
                setOpenCancelBalanceModal(true);
                setSelectedRow(rowData);
              }}
            />
          </>
        )}
        {!isCancelled && rowData?.zatca_status == 'REPORTED' && (
          <FaHistory
            className='text-teal-500 cursor-pointer hover:text-teal-600'
            size={16}
            title={t('ZATCA History')}
            onClick={() => getZatcaHistory(rowData?.deposit_invoice_number || rowData?.invoice_number)}
          />
        )}
        {rowData?.zatca_status != 'REPORTED' && hasPermission('zatca', 'get') && !isCancelled && isSendToZatca && (
          <IoIosSend
            className='text-green-500 cursor-pointer'
            title={t('Send to zatca')}
            size={20}
            onClick={() => {
              setOpenSendToZatcaModal(true);
              setSelectedInvoice(rowData);
            }}
          />
        )}
      </div>
    );
  };

  const depositActions = {
    header: t('Actions'),
    body: actionBodyTempelate
  };

  const columnsData = [depositActions, ...DepositColumns(t)];
  return (
    <div>
      <BillingFilters
        applyFilters={applyFilters}
        setSearchQuery={setSearchQuery}
        fetchInitialData={fetchInitialData}
        searchQuery={searchQuery}
        payloadData={payload}
        setPayloadData={setPayload}
        refetch={refetch}
        invoiceType='deposit'
        resetFilters={() => {
          resetPayload();
          setSearchQuery('');
        }}
      />
      <div>
        <CustomTable
          isLoading={loading}
          data={data?.data || []}
          columns={columnsData}
          rowClassName={row => (row?.status ? '!bg-red-100' : '')}
        />
        {data?.data?.length > 0 && !loading && (
          <CustomPagination
            currentPage={data?.page || payload?.page || 1}
            rowsPerPage={data?.per_page || payload?.per_page || 20}
            totalRecords={data?.totalRecords}
            totalPages={data?.totalPages}
            onPageChange={onPageChange}
          />
        )}
      </div>
      <ReuseableModal
        isVisible={openCancelBalanceModal}
        onClose={() => setOpenCancelBalanceModal(false)}
        title={t(`Cancel Deposit Balance of ${selectedRow?.patient_name}`)}
        position='top'
      >
        <CancelDepositBalanceForm
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          payloadData={payload}
          fetchInitialData={fetchInitialData}
          setOpenCancelBalanceModal={setOpenCancelBalanceModal}
        />
      </ReuseableModal>

      <ConfirmationModal
        visible={openSendToZatcaModal}
        setVisible={setOpenSendToZatcaModal}
        header={'Confirmation'}
        message={`Are you sure you want to send deposit invoice ${selectedInvoice?.deposit_invoice_number} to Zatca?`}
        acceptLabel={'Send To Zatca'}
        rejectLabel={'Cancel'}
        onAccept={handleSendToZatca}
        isLoading={zatcaLoading}
      />

      <ReuseableModal
        isVisible={openZatcaHistoryModal}
        title={t(`ZATCA Submission History Of #${selectedInvoice}`)}
        HeaderIcon={FaHistory}
        position='top'
        onClose={() => {
          setOpenZatcaHistoryModal(false);
          setZatcaHistoryData(null);
          setSelectedInvoice(null);
        }}
        className='w-full md:w-[90%] lg:w-[80%]'
      >
        <ZatcaHistoryModal historyData={zatcaHistoryData} loading={zatcaHistoryLoading} />
      </ReuseableModal>
    </div>
  );
}

export default DepositList;
